import { FONT_OPTIONS } from './fonts'
import { responsiveSize } from './textStyles'

/**
 * Texte riche a la lettre pres : on ne garde que des balises et des styles
 * connus (liste blanche) avant d'afficher ou d'enregistrer quoi que ce soit.
 */

const ALLOWED_TAGS = new Set(['SPAN', 'B', 'STRONG', 'I', 'EM', 'U', 'BR'])
const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'NOSCRIPT', 'TEMPLATE'])
const BLOCK_TAGS = new Set(['DIV', 'P', 'LI'])

export interface SanitizeOptions {
  /** Convertit les tailles en px en tailles adaptatives (affichage public) */
  responsive?: boolean
  /** Ajoute !important pour passer devant les regles globales du site */
  important?: boolean
}

export interface RichText {
  html: string
  plain: string
}

function firstFamily(value: string): string {
  return value.split(',')[0].replace(/["']/g, '').trim().toLowerCase()
}

function sanitizeStyle(el: HTMLElement, opts: SanitizeOptions): string {
  const s = el.style
  const imp = opts.important ? ' !important' : ''
  const out: string[] = []

  if (s.fontSize && /^\d+(\.\d+)?px$/.test(s.fontSize)) {
    const px = parseFloat(s.fontSize)
    if (px >= 6 && px <= 300) {
      out.push(`font-size: ${opts.responsive ? responsiveSize(px) : `${px}px`}${imp}`)
    }
  }

  if (s.fontFamily) {
    const name = firstFamily(s.fontFamily)
    const option = FONT_OPTIONS.find((f) => firstFamily(f.stack) === name)
    if (option) out.push(`font-family: ${option.stack}${imp}`)
  }

  if (s.fontWeight && /^(100|200|300|400|500|600|700|800|900|bold|normal)$/.test(s.fontWeight)) {
    out.push(`font-weight: ${s.fontWeight}${imp}`)
  }

  if (s.fontStyle === 'italic' || s.fontStyle === 'normal') {
    out.push(`font-style: ${s.fontStyle}${imp}`)
  }

  if (s.color && /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\))$/i.test(s.color)) {
    out.push(`color: ${s.color}${imp}`)
  }

  const deco = s.textDecorationLine || s.textDecoration
  if (deco && /^(underline|line-through|none)$/.test(deco.trim())) {
    out.push(`text-decoration: ${deco.trim()}${imp}`)
  }

  return out.join('; ')
}

function walk(src: Node, dest: Node, doc: Document, opts: SanitizeOptions) {
  src.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      dest.appendChild(doc.createTextNode(child.textContent || ''))
      return
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return

    const el = child as HTMLElement
    if (DROP_TAGS.has(el.tagName)) return

    if (el.tagName === 'BR') {
      dest.appendChild(doc.createElement('br'))
      return
    }

    if (!ALLOWED_TAGS.has(el.tagName)) {
      // Balise inconnue : on garde son texte, pas la balise
      if (BLOCK_TAGS.has(el.tagName) && dest.childNodes.length > 0) {
        dest.appendChild(doc.createElement('br'))
      }
      walk(el, dest, doc, opts)
      return
    }

    if (el.tagName === 'SPAN') {
      const style = sanitizeStyle(el, opts)
      if (!style) {
        walk(el, dest, doc, opts)
        return
      }
      const span = doc.createElement('span')
      span.setAttribute('style', style)
      walk(el, span, doc, opts)
      dest.appendChild(span)
      return
    }

    const out = doc.createElement(el.tagName.toLowerCase())
    walk(el, out, doc, opts)
    dest.appendChild(out)
  })
}

function parse(html: string): Document {
  return new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html')
}

export function sanitizeRichHtml(html: string, opts: SanitizeOptions = {}): string {
  if (!html) return ''
  const doc = parse(html)
  const container = doc.createElement('div')
  walk(doc.body, container, doc, opts)
  return container.innerHTML
}

export function escapeToHtml(plain: string): string {
  return (plain || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\r?\n/g, '<br>')
}

export function htmlToPlain(html: string): string {
  const doc = parse(html)
  doc.body.querySelectorAll('br').forEach((br) => br.replaceWith('\n'))
  return (doc.body.textContent || '').replace(/\u00a0/g, ' ').replace(/\n+$/, '')
}

/** Vrai si le HTML contient autre chose que du texte et des retours a la ligne */
export function hasFormatting(html: string): boolean {
  return /<(?!br\b)[a-z]/i.test(html)
}

export type InlineCss = Partial<{
  fontSize: string
  fontFamily: string
  fontWeight: string
  fontStyle: string
  color: string
  textDecoration: string
}>

const FORMAT_TAGS = new Set(['SPAN', 'B', 'STRONG', 'I', 'EM', 'U'])

/**
 * Isole exactement les caracteres selectionnes dans des noeuds de texte a part
 * (decoupe les noeuds de texte aux bornes de la selection) et les renvoie.
 */
function splitSelectedText(editor: HTMLElement, range: Range): Text[] {
  const parts: { node: Text; start: number; end: number }[] = []
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT)
  let current = walker.nextNode()
  while (current) {
    const node = current as Text
    if (range.intersectsNode(node)) {
      const length = node.data.length
      const start = node === range.startContainer ? range.startOffset : 0
      const end = node === range.endContainer ? range.endOffset : length
      if (end > start) parts.push({ node, start, end })
    }
    current = walker.nextNode()
  }

  return parts.map(({ node, start, end }) => {
    let target = node
    if (end < target.data.length) target.splitText(end)
    if (start > 0) target = target.splitText(start)
    return target
  })
}

/** Garde un seul noeud dans son parent de mise en forme : coupe le parent en 3 si besoin. */
function isolateInParent(editor: HTMLElement, target: Node) {
  const parent = target.parentNode as HTMLElement | null
  if (!parent || parent === editor || !FORMAT_TAGS.has(parent.tagName)) return
  if (parent.childNodes.length === 1) return

  const before = parent.cloneNode(false)
  const after = parent.cloneNode(false)
  while (parent.firstChild && parent.firstChild !== target) before.appendChild(parent.firstChild)
  while (target.nextSibling) after.appendChild(target.nextSibling)
  if (before.childNodes.length > 0) parent.parentNode?.insertBefore(before, parent)
  if (after.childNodes.length > 0) parent.parentNode?.insertBefore(after, parent.nextSibling)
}

function rangeAround(first: Node, last: Node): Range {
  const range = document.createRange()
  range.setStartBefore(first)
  range.setEndAfter(last)
  return range
}

function selectRange(range: Range) {
  const selection = window.getSelection()
  if (!selection) return
  selection.removeAllRanges()
  selection.addRange(range)
}

/** Applique un style aux caracteres de la selection (mot, lettre ou phrase). */
export function applyInlineStyle(editor: HTMLElement, range: Range, css: InlineCss): Range | null {
  if (range.collapsed) return null
  const texts = splitSelectedText(editor, range)
  if (texts.length === 0) return null

  const wrapped: Node[] = []
  texts.forEach((text) => {
    isolateInParent(editor, text)
    const parent = text.parentNode as HTMLElement
    let span: HTMLElement
    if (parent !== editor && parent.tagName === 'SPAN' && parent.childNodes.length === 1) {
      span = parent
    } else {
      span = document.createElement('span')
      parent.insertBefore(span, text)
      span.appendChild(text)
    }
    Object.entries(css).forEach(([key, value]) => {
      ;(span.style as any)[key] = value
    })
    wrapped.push(span)
  })

  const next = rangeAround(wrapped[0], wrapped[wrapped.length - 1])
  selectRange(next)
  return next.cloneRange()
}

/** Italique / souligne : active si une partie de la selection ne l'est pas, sinon desactive. */
export function toggleInlineStyle(
  editor: HTMLElement,
  range: Range,
  kind: 'italic' | 'underline',
): Range | null {
  if (range.collapsed) return null
  const texts = splitSelectedText(editor, range)
  if (texts.length === 0) return null

  const isOn = (node: Node) => {
    const el = node.parentElement
    if (!el) return false
    const computed = window.getComputedStyle(el)
    return kind === 'italic'
      ? computed.fontStyle === 'italic'
      : (computed.textDecorationLine || computed.textDecoration || '').includes('underline')
  }
  const turnOn = !texts.every(isOn)

  const wrapped: Node[] = []
  texts.forEach((text) => {
    isolateInParent(editor, text)
    const parent = text.parentNode as HTMLElement
    let span: HTMLElement
    if (parent !== editor && parent.tagName === 'SPAN' && parent.childNodes.length === 1) {
      span = parent
    } else {
      span = document.createElement('span')
      parent.insertBefore(span, text)
      span.appendChild(text)
    }
    if (kind === 'italic') span.style.fontStyle = turnOn ? 'italic' : 'normal'
    else span.style.textDecoration = turnOn ? 'underline' : 'none'
    wrapped.push(span)
  })

  const next = rangeAround(wrapped[0], wrapped[wrapped.length - 1])
  selectRange(next)
  return next.cloneRange()
}

/** Retire la mise en forme des caracteres selectionnes uniquement. */
export function clearInlineStyle(editor: HTMLElement, range: Range): Range | null {
  if (range.collapsed) return null
  const texts = splitSelectedText(editor, range)
  if (texts.length === 0) return null

  const first = texts[0]
  const last = texts[texts.length - 1]
  texts.forEach((text) => {
    let guard = 0
    while (guard++ < 10) {
      const parent = text.parentNode as HTMLElement | null
      if (!parent || parent === editor || !FORMAT_TAGS.has(parent.tagName)) break
      isolateInParent(editor, text)
      const holder = text.parentNode as HTMLElement
      holder.replaceWith(text)
    }
  })

  const next = rangeAround(first, last)
  selectRange(next)
  return next.cloneRange()
}
